import { getSeriesWindowing } from "../../utils/dicomVoi";
import { useNavigate } from "react-router-dom";
import { useAtomValue, useSetAtom } from "jotai";

import { studyRowId } from "../../atoms/labelingAtoms";
import type { Worklist } from "../../atoms/labelingAtoms";
import type { StudyMetadataResponse } from "../../utils/dicomMetadataLoader";
import type { FindingJumpTarget } from "../../hooks/useReportQuery";
import {
  hangingProtocolActionsAtom,
  slotAssignmentsSelector,
} from "../../atoms/hangingProtocolAtoms";
import { SLOT_IDS, DEFAULT_SLOT } from "../../utils/hangingProtocolService";
import { shouldAutoInvert } from "../../utils/dicomMetadataLoader";
import PrototypeReport from "../PrototypeReport";
import type {
  StudyInitialDocContext,
  StudySignConfirmation,
  StudySignContext,
} from "../PrototypeReport";
import { NavigationButtons } from "../common";
import { WorkListButton } from "./WorkListButton";
import { StudyTimerPill } from "../study/StudyTimerPill";
import type { ReaderReportContext } from "../../hooks/useStudyEventLog";

interface ReportingRightPanelProps {
  readerContext?: ReaderReportContext;
  worklist: Worklist;
  studyIndex: number;
  studyMetadata: StudyMetadataResponse | undefined;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  /** When set, overrides the worklist row's `template_mode`. The
   *  reader-study wiring uses this to force `"blank"` for the no-AI arm. */
  templateModeOverride?: "ai" | "blank";
  /** Commits the reader signature. Carries the saved document revision +
   *  template metadata + difficulty rating; reader-study wiring uses
   *  these for the atomic /api/study/sign POST. */
  onSigned?: (ctx: StudySignContext) => Promise<void> | void;
  /** Fires once per study when the editor's initial doc settles. */
  onInitialDoc?: (ctx: StudyInitialDocContext) => void;
  /** Optional sign-confirmation gate (replaces the default browser
   *  confirm — used by the difficulty modal). */
  confirmSign?: () => Promise<StudySignConfirmation>;
  /** Reader-study timer state. `null` startedAt → not started yet (lock
   *  screen is up upstream); paused → idle detection has fired. */
  studyTimerStartedAt?: number | null;
  studyTimerPaused?: boolean;
  /** Untimed acclimation case — the pill shows "not timed" instead of a clock. */
  studyIsPractice?: boolean;
}

export function ReportingRightPanel({
  readerContext,
  worklist,
  studyIndex,
  studyMetadata,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  templateModeOverride,
  onSigned,
  onInitialDoc,
  confirmSign,
  studyTimerStartedAt,
  studyTimerPaused,
  studyIsPractice,
}: ReportingRightPanelProps) {
  const navigate = useNavigate();
  const slotAssignments = useAtomValue(slotAssignmentsSelector);
  const dispatchHangingProtocol = useSetAtom(hangingProtocolActionsAtom);

  const study = worklist.studies[studyIndex];
  const templateMode = templateModeOverride ?? (study.template_mode === "blank" ? "blank" : "ai");
  const studyId = studyRowId(study);

  // Mirror CaseViewer's handleFindingSelectSimplified so arrow-jump + tab-jump
  // on AI findings drive the hanging protocol viewports.
  const handleFindingClick = ({ seriesId, sliceIndex }: FindingJumpTarget) => {
    const existingSlot = SLOT_IDS.find((id) => slotAssignments[id] === seriesId);
    if (existingSlot) {
      dispatchHangingProtocol({ type: "CHANGE_SLICE", slotId: existingSlot, sliceIndex });
      return;
    }
    const meta = studyMetadata?.series_metadata?.[seriesId];
    const seriesInstances = studyMetadata?.series?.[seriesId];
    // Seed the jump target into the swap itself (pendingJump) rather than a
    // requestAnimationFrame'd CHANGE_SLICE. The rAF raced StackViewport's async
    // setStack and landed the jump mid-series on the weak-GPU fallback — same
    // fix as selectFinding (Mike Moritz, 2026-05-29).
    dispatchHangingProtocol({
      type: "SET_SERIES",
      slotId: DEFAULT_SLOT,
      seriesId,
      sliceIndex,
      windowing: meta ? getSeriesWindowing(meta) : undefined,
      isInverted: shouldAutoInvert(seriesInstances),
    });
  };

  return (
    <div className="flex flex-col h-full bg-bg text-fg select-none">
      <div className="px-3 pt-3 pb-2 border-b border-border-strong shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-fg-muted uppercase tracking-wider">
            Reporting · {templateMode} template
          </span>
          <div className="flex items-center gap-2">
            {studyTimerStartedAt !== undefined && (
              <StudyTimerPill
                startedAt={studyTimerStartedAt}
                paused={!!studyTimerPaused}
                isPractice={studyIsPractice}
              />
            )}
            <WorkListButton
              onClick={() =>
                navigate(`/labeling/reporting/${encodeURIComponent(worklist.project_id)}`)
              }
              compact
            />
            <span className="text-xs text-fg-muted">
              {studyIndex + 1} / {worklist.studies.length}
            </span>
          </div>
        </div>
        <div className="text-xs text-fg-muted truncate">
          {study.patient_id} · {study.accession}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <PrototypeReport
          readerContext={readerContext}
          studyId={studyId}
          sessionMode="labeling-reporting"
          templateMode={templateMode}
          onFindingClick={handleFindingClick}
          onSigned={onSigned}
          onInitialDoc={onInitialDoc}
          confirmSign={confirmSign}
          hideOriginalReport
        />
      </div>
      <div className="px-3 pb-3 pt-2 border-t border-border-strong shrink-0">
        <NavigationButtons
          onPrevious={onPrevious}
          onNext={onNext}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
        />
      </div>
    </div>
  );
}
